import moment from "moment/moment";

import { CROP_TYPE_FCPO, CategorizedFieldCropRecommendation, CropItemData } from "models";

import { GROUP_TYPE, PRACTICE_TYPE, TimelineCalendarItemsProps } from "pages/app/field-timeline/components/FieldDetailsTimeline.tsx";

const createRecommendationPlaceholders = (fieldCropRecommendationsResponse: CategorizedFieldCropRecommendation[], fieldId: string): CropItemData[] => {
  const recommendationPlaceholders = fieldCropRecommendationsResponse
    .map((categorizedRecommendations) => {
      const coverCrop = categorizedRecommendations.catchCrop.recommendations.length > 0 ? categorizedRecommendations.catchCrop : null;
      const undersowingCrop = categorizedRecommendations.undersowing.recommendations.length > 0 ? categorizedRecommendations.undersowing : null;

      const output: CropItemData[] = [];
      if (coverCrop && coverCrop.harvestDate && coverCrop.seedingDate) {
        output.push({
          cropName: "",
          hasResidue: false,
          payout: Math.max(coverCrop.recommendations[0].payoutDifference.withResidue ?? 0, coverCrop.recommendations[0].payoutDifference.withoutResidue),
          cropType: CROP_TYPE_FCPO.COVER,
          year: categorizedRecommendations.year,
          isBaseline: false,
          isMandatory: false,
          isConfirmed: false,
          fertilizerManagements: [],
          yieldTonsPerHa: null,
          recommendations: {
            fieldId,
            harvestEndDate: coverCrop.harvestDate,
            seedingStartDate: coverCrop.seedingDate,
            cropRecommendations: coverCrop.recommendations,
          },
        });
      }
      if (undersowingCrop && undersowingCrop.harvestDate && undersowingCrop.seedingDate) {
        output.push({
          cropName: "",
          hasResidue: false,
          payout: Math.max(
            undersowingCrop.recommendations[0].payoutDifference.withResidue ?? 0,
            undersowingCrop.recommendations[0].payoutDifference.withoutResidue
          ),
          cropType: CROP_TYPE_FCPO.UNDERSOWING,
          year: categorizedRecommendations.year,
          isBaseline: false,
          isMandatory: false,
          isConfirmed: false,
          fertilizerManagements: [],
          yieldTonsPerHa: null,
          recommendations: {
            fieldId,
            harvestEndDate: undersowingCrop.harvestDate,
            seedingStartDate: undersowingCrop.seedingDate,
            cropRecommendations: undersowingCrop.recommendations,
          },
        });
      }

      return output;
    })
    .flat();
  return recommendationPlaceholders;
};

export const createRecommendations = (
  fieldCropRecommendationsResponse: CategorizedFieldCropRecommendation[],
  coverCropPlaceholderItems: TimelineCalendarItemsProps<CropItemData>[],
  fieldId: string,
  groupId?: string
): TimelineCalendarItemsProps<CropItemData>[] => {
  // for the undersowing crops, we return data fetched from the old recommendation system. For cover crops, we return the CMC placeholder card
  const recommendationPlaceholders = createRecommendationPlaceholders(fieldCropRecommendationsResponse, fieldId);

  const recommendationItems: TimelineCalendarItemsProps<CropItemData>[] =
    recommendationPlaceholders.map((recommendation, i) => {
      const recommendationItem: CropItemData = {
        cropName: recommendation.cropName,
        hasResidue: recommendation.hasResidue,
        payout: recommendation.payout,
        cropType: recommendation.cropType,
        year: recommendation.year,
        isBaseline: recommendation.isBaseline,
        isMandatory: recommendation.isMandatory,
        isConfirmed: recommendation.isConfirmed,
        fertilizerManagements: recommendation.fertilizerManagements,
        yieldTonsPerHa: recommendation.yieldTonsPerHa,
        recommendations: recommendation.recommendations,
      };

      return {
        id: `${fieldId}-recommendation-${recommendationItem.cropType}-${recommendationItem.year}-${i}`,
        group: groupId ?? GROUP_TYPE.SIDE_CROP,
        title: "",
        start_time: moment(recommendation.recommendations?.seedingStartDate),
        end_time: moment(recommendation.recommendations?.harvestEndDate),
        practiceType: PRACTICE_TYPE.SIDE_CROP_RECOMMENDATION,
        canResize: false,
        canMove: false,
        className: "placeholder-crop recommendation-crop",
        data: recommendationItem,
      };
    }) || [];

  const coverCropRecommendationPlaceholders: TimelineCalendarItemsProps<CropItemData>[] = coverCropPlaceholderItems.map((item, i) => ({
    ...item,
    id: `${fieldId}-recommendation-${item.data.cropType}-${item.data.year}-${i}`,
    className: "placeholder-crop recommendation-crop",
    practiceType: PRACTICE_TYPE.SIDE_CROP_RECOMMENDATION,
    isPlaceholder: false,
    data: {
      ...item.data,
      recommendations: {
        fieldId,
        harvestEndDate: item.start_time.toISOString(),
        seedingStartDate: item.end_time.toISOString(),
        cropRecommendations: [],
      },
    },
  }));

  return [...recommendationItems, ...coverCropRecommendationPlaceholders];
};
